/**
 * # PanelCarousel
 *
 * Panel Carousel, as used in KPI, summary bar.
 * unofficial component
 *
 * @module components/panel-carousel.js
 */
import BaseComponent from './base';
import addClass from '../helpers/dom/add-class';
import removeClass from '../helpers/dom/remove-class';
import debounce from '../helpers/util/debounce';
import hasClass from '../helpers/dom/has-class';
import outerWidth from '../helpers/dom/outer-width';
import offset from '../helpers/dom/offset';
import { get as getBreakpoint } from '../helpers/dom/breakpoint';

class PanelCarousel extends BaseComponent {


  /**
   * PanelCarousel constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    super(el, params);

    if (!el) {
      return;
    }

    this._bindEventListenerCallbacks();
    this._addEventListeners();
    this._initCarousel();
  }


  /**
   * Creates the carousel controls.
   */
  _addControls() {
    this._forwards = document.createElement('div');
    addClass(this._forwards, 'spark-panel-carousel__forwards');
    this._forwards.innerHTML = '<button type="button" aria-label=' + this.nextLabel + '><i class="spark-icon-arrow-chevron-right spark-icon--fill"></i></button>';

    this._backwards = document.createElement('div');
    addClass(this._backwards, 'spark-panel-carousel__backwards');
    this._backwards.innerHTML = '<button type="button" aria-label=' + this.previousLabel + '><i class="spark-icon-arrow-chevron-left spark-icon--fill"></i></button>';

    this.el.appendChild(this._backwards);
    this.el.appendChild(this._forwards);

    if (this._hasTabs === true) {
      this._forwards.querySelector('button').setAttribute('tabIndex', -1);
      this._backwards.querySelector('button').setAttribute('tabIndex', -1);
      this._panelCarouselList.addEventListener('click', this._onTabListClickBound);
    }

    addClass(this._backwards, 'spark-panel-carousel__limit');
  }


  /**
   * Remove event listeners
   */
  _removeEventListeners() {
    this._forwards.removeEventListener('click', this._onControlClickBound);
    this._backwards.removeEventListener('click', this._onControlClickBound);
    this._panelCarouselList.removeEventListener('touchstart', this._onMoveStartBound);
    this.el.removeEventListener('mousedown', this._onMoveStartBound);
    this._panelCarouselList.removeEventListener('keyup', this._onKeyUpBound);
    window.removeEventListener('scroll', this._onScrollBound);
    window.removeEventListener('resize', this._onResizeBound);
  }


  /**
   * Bind and add Carousel listeners
   */
  _addCarouselListeners() {
    this._onKeyUpBound = this._onKeyUp.bind(this);
    this._onControlClickBound = this._onControlClick.bind(this);
    this._onMoveStartBound = this._onMoveStart.bind(this);
    this._onMoveBound = this._onMove.bind(this);
    this._onMoveEndBound = this._onMoveEnd.bind(this);

    this._forwards.addEventListener('click', this._onControlClickBound);
    this._backwards.addEventListener('click', this._onControlClickBound);
    this._panelCarouselList.addEventListener('touchstart', this._onMoveStartBound);
    this.el.addEventListener('mousedown', this._onMoveStartBound);
    this._panelCarouselList.addEventListener('keyup', this._onKeyUpBound);
  }


  /**
   * Remove Carousel listeners
   */
  _removeCarouselListeners() {
    this._forwards.removeEventListener('click', this._onControlClickBound);
    this._backwards.removeEventListener('click', this._onControlClickBound);
    this.el.removeEventListener('mousedown', this._onMoveStartBound);
    this._panelCarouselList.removeEventListener('keyup', this._onKeyUpBound);
  }


  /**
   * Add move listeners.
   * @param {Event} _event either a TouchEvent or a MouseEvent
   */
  _addMoveListeners(_event) {
    if (_event.touches) {
      this._panelCarouselList.addEventListener('touchmove', this._onMoveBound);
      this._panelCarouselList.addEventListener('touchend', this._onMoveEndBound);
    } else {
      window.addEventListener('mouseup', this._onMoveEndBound);
      window.addEventListener('mousemove', this._onMoveBound);
    }
  }


  /**
   * Remove move listeners
   * @param  {Event} _event MouseEvent or TouchEvent
   */
  _removeMoveListeners(_event) {
    if (_event.touches) {
      this._panelCarouselList.removeEventListener('touchmove', this._onMoveBound);
      this._panelCarouselList.removeEventListener('touchend', this._onMoveEndBound);
    } else {
      window.removeEventListener('mouseup', this._onMoveEndBound);
      window.removeEventListener('mousemove', this._onMoveBound);
    }
  }


  /**
   * Add bound event listeners
   */
  _addEventListeners() {
    window.addEventListener('scroll', this._onScrollBound);
    window.addEventListener('resize', this._onResizeBound);
    this.el.addEventListener('scroll', function(_event) {
      if (this.scrollLeft !== 0) {
        this.scrollLeft = 0;
      }
      _event.stopImmediatePropagation();
      _event.preventDefault();
      return false;
    });
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onScrollBound = this._onScroll.bind(this);
    this._onResizeBound = debounce(this._onResize.bind(this), 90);
  }


  /**
   * Store a reference to the tabs list, each tab and each panel.
   * Set which tab is active, or use the first.
   * @param {Element} el
   */
  _cacheElements(el) {
    this.el = el;
    this._panelCarouselList = this.el.querySelector('.spark-panel-carousel__list');
    this._panelCarouselItems = this.el.querySelectorAll('.spark-panel-carousel__item');
    this.listTransitionDuration = window.getComputedStyle(this._panelCarouselList).getPropertyValue('transition-duration') || '800ms';
  }


  /**
   * Calculate the width of the Panel Carousel Items. widths are not static, may change based on screen size.
   */
  _calcWidth() {
    this._panelCarouselWidth = outerWidth(this._panelCarouselList);
    this._panelCarouselItemsWidth = 0;

    for (let i = this._panelCarouselItems.length - 1; i > -1; i--) {
      this._panelCarouselItemsWidth += outerWidth(this._panelCarouselItems[i]);
      void this._panelCarouselItems.offsetWidth;
    }

    this.maxX = this._panelCarouselWidth - this._panelCarouselItemsWidth;

    // <= 0 means its part of a hidden div
    if (this._panelCarouselItemsWidth > this._panelCarouselWidth || this._panelCarouselWidth <= 0) {
      this._overflow = true;
      this.el.setAttribute('data-overflow', true);
    } else {
      this._overflow = false;
      this.el.removeAttribute('data-overflow');
    }
  }


  /**
   * Calculate the current Break point, update flags based on value
   *
   */
  _calcBreakpoint() {
    this.currentBreakpoint = getBreakpoint(window.innerWidth);
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
    const currentBreakIndex = breakpoints.indexOf(this.currentBreakpoint);
    const noCarouselBreakIndex = breakpoints.indexOf(this.noCarouselBreakPoint);
    const controlBreakIndex = breakpoints.indexOf(this.controlBreakPoint);

    // if not using the carousel, don't monitor touch events
    if (currentBreakIndex <= noCarouselBreakIndex && noCarouselBreakIndex !== -1) {
      this._useCarousel = false;
    } else {
      this._useCarousel = true;
    }

    if (currentBreakIndex <= controlBreakIndex) {
      this._useControls = false;
    } else {
      this._useControls = true;
    }
  }


  /**
   * Initialize the carousel slider.
   */
  _initCarousel() {
    this.currentBreakpoint = getBreakpoint(window.innerWidth);
    this._calcWidth();
    this._calcBreakpoint();
    this._addControls();
    this._showControls();
    this._addCarouselListeners();
  }


  /**
   * onScroll, if there is padding, if a carousel is in a fixed panel it will expand to fill 100%, need to watch if the isFixed class is applied and recalculate the width
   */
  _onScroll() {

    // if you have already done the calculation for the change in fixed state, don't recalc for every scroll event
    if (this.isFixed === hasClass(this.el, 'is-fixed-top')) {
      return;
    }

    if (hasClass(this.el, 'is-fixed-top')) {
      this.isFixed = true;
    } else {
      this.isFixed = false;
    }

    this._calcWidth();
    if (this._forwardLimit === true) {
      this._updateCarousel(this.maxX);
    }
  }


  /**
   * See if you are tabbing through the carousel. This is only attached when it's a tabbed variation.
   * @param  {KeyEvent} _event KeyEvent
   *
   */
  _onKeyUp(_event) {
    const tab = _event.target;

    if (_event.keyCode !== 9) {
      return;
    }

    // turn of transition for rapid tabbing.
    this._panelCarouselList.style['transition-duration'] = '0s';

    if (_event.shiftKey === true) {
      if (offset(tab).left <= outerWidth(this._backwards)) {
        this._backwards.click();
      } else if (offset(tab).left > outerWidth(this._panelCarouselList)) {
        this._forwardLimit = true;
        this._calcWidth();
        this._updateCarousel(this.maxX);
      }
    } else {
      if (offset(tab).left + outerWidth(tab) > outerWidth(this._panelCarouselList) - outerWidth(this._forwards)) {
        if (this._forwardLimit !== true) {
          this._forwards.click();
        }
      } else if (offset(tab).left + outerWidth(tab) < 0) {
        this._backwardLimit = true;
        this._updateCarousel(0);
      }
    }

    // turn on transition
    this._panelCarouselList.style['transition-duration'] = this.listTransitionDuration;
  }


  /**
   * Window resize event, keeps the carousel fresh... debounced from the listener. An event is passed to the method, but not used.
   */
  _onResize() {
    this._calcWidth();
    this._calcBreakpoint();
    this._updateCarousel(0);
    this._calcWidth();
  }


  /**
   * Track touch end event, re-add transition
   * @param  {TouchEvent} _event touchmove
   */
  _onMoveEnd(_event) {
    let xPosition;
    let screenX = this._startX;

    // touch event or mouse event
    if (_event.changedTouches) {
      screenX = _event.changedTouches[0].screenX;
      xPosition = (screenX - this._startX) + this._positionX;
    } else {
      screenX = _event.screenX;
      xPosition = (screenX - this._startX) + this._positionX;
    }

    this._moving = false;
    this._removeMoveListeners(_event);
    this._panelCarouselList.style['transition-duration'] = this.listTransitionDuration;

    // if difference is 0 then it was a click not a move;
    // a press looks like a move, this avoids it looking like a forward or backward click when it's a tab click
    if (this._useCarousel === false || Math.round(screenX - this._startX) === 0) {
      return;
    }
    if (xPosition > this._positionX) {
      this._backwards.click();
    } else {
      this._forwards.click();
    }
  }


  /**
   * Track touch move event. Moves the bar along with the touch movement.
   * @param  {TouchEvent} _event touchmove
   */
  _onMove(_event) {

    // don't track if the move hasn't started
    if (this._moving === false) {
      return;
    }
    let xPosition;

    // test if has touch events or if it's a mouse event
    if (_event.changedTouches) {
      xPosition = (_event.changedTouches[0].screenX - this._startX) + this._positionX;
    } else {
      xPosition = (_event.screenX - this._startX) + this._positionX;
    }
    if (this._useCarousel === false) {
      return;
    }

    // the max is 0
    if (xPosition > 0) {
      xPosition = 0;
    }

    // maxX is a negative number, which is why this looks odd;
    if (xPosition < this.maxX) {
      xPosition = this.maxX;
    }

    // need to remove the transition or the move will try and transition rather than just going along with the move event.
    this._panelCarouselList.style['transition-duration'] = '0s';
    this._panelCarouselList.style.transform = 'translateX(' + xPosition + 'px)';
    if (_event) {
      _event.preventDefault();
    }
  }


  /**
   * Track touch start event. Calculates the maximum distance that you can scroll based on number of items and current position.
   * @param  {TouchEvent} _event touchmove
   */
  _onMoveStart(_event) {
    if (this._useCarousel === false) {
      return;
    }
    this._moving = true;
    this._addMoveListeners(_event);
    if (_event.changedTouches) {
      this._startX = _event.changedTouches[0].screenX;
      this._startY = _event.changedTouches[0].screenY;
    } else {
      this._startX = _event.screenX;
      this._startY = _event.screenY;
    }

    this._elRect = this.el.getBoundingClientRect();

    if (this._useControls === true) {
      this.maxX = -(this._panelCarouselItemsWidth - this._elRect.width + outerWidth(this._forwards) + outerWidth(this._backwards));
    } else {
      this.maxX = this._elRect.width - this._panelCarouselItemsWidth;
    }

    this.maxX = Math.round(this.maxX);
  }


  /**
   * Update the carousel position
   *
   * @param  {type} xPosition
   */
  _updateCarousel(xPosition) {
    if (xPosition === this.maxX) {
      this._forwardLimit = true;
    } else {
      this._forwardLimit = false;
    }

    if (xPosition === 0) {
      this._backwardLimit = true;
    } else {
      this._backwardLimit = false;
    }

    this._panelCarouselList.style.transform = "translateX(" + xPosition + "px)";
    this._positionX = xPosition;
    this._showControls();
  }


  /**
   * Identify which carousel button was clicked trigger correct arrow
   * @param  {clickEvent} _event click
   */
  _onControlClick(_event) {
    _event.preventDefault();
    const button = _event.currentTarget;
    let xPosition = 0;
    if (hasClass(button, 'spark-panel-carousel__forwards')) {
      if (this.singleCarouselAdvance === false && this._useControls === true) {
        xPosition = this._setForwardClick(_event);
      } else {
        xPosition = this._singleForwardClick(_event);
      }
    } else if (hasClass(button, 'spark-panel-carousel__backwards')) {
      if (this.singleCarouselAdvance === false && this._useControls === true) {
        xPosition = this._setBackwardClick(_event);
      } else {
        xPosition = this._singleBackwardClick(_event);
      }
    } else {
      // not our click;
      return;
    }

    this._updateCarousel(Math.round(xPosition));
    _event.preventDefault();
  }


  /**
   * Advance the carousel one set.
   * @param  {clickEvent} _event click
   * @return {Number}
   */
  _setForwardClick(_event) {
    const button = _event.currentTarget;
    const buttonLeft = offset(button).left;
    const buttonWidth = outerWidth(button);
    const left = offset(this._panelCarouselItems[0]).left;
    const listBoxWidth = outerWidth(this._panelCarouselList);
    const max = buttonLeft;
    let xPosition = 0;
    let totalWidth = 0;

    this._backwardLimit = false;

    // recalculate the total width incase content was updated outside of resize.
    for (let i = 0; i < this._panelCarouselItems.length; i++) {
      totalWidth += outerWidth(this._panelCarouselItems[i]);
    }
    this.maxX = Math.round(-totalWidth + listBoxWidth);
    xPosition = this.maxX;

    for (let i = 0; i < this._panelCarouselItems.length; i++) {
      if (offset(this._panelCarouselItems[i]).left + outerWidth(this._panelCarouselItems[i]) - buttonWidth > max) {
        xPosition = left - (offset(this._panelCarouselItems[i]).left - buttonWidth);
        if (-xPosition > totalWidth - listBoxWidth) {
          xPosition = -totalWidth + listBoxWidth;
          this.maxX = Math.round(xPosition);
        }
        break;
      }
    }

    // if you can't go any further set the forwardLimit to true
    if (this.maxX === xPosition) {
      this._forwardLimit = true;
    }

    return xPosition;
  }


  /**
   * Reverse the carousel one set of items.
   * @param  {clickEvent} _event click
   * @return {Number}
   */
  _setBackwardClick(_event) {
    const button = _event.currentTarget;
    let xPosition = outerWidth(this._panelCarouselList);
    this._forwardLimit = false;

    for (let i = this._panelCarouselItems.length - 1; i > -1; i--) {
      if (offset(this._panelCarouselItems[i]).left <= offset(this.el).left) {
        xPosition -= outerWidth(this._panelCarouselItems[i]);
      }
    }

    xPosition -= outerWidth(button);

    // if you can't go back any further, set to 0
    if (xPosition >= -outerWidth(button)) {
      xPosition = 0;
    }

    return xPosition;
  }


  /**
   * Shows or hides controls based on screen width, specified break points.
   */
  _showControls() {
    // only need controls if there is an overflow and if you are still using the carousel__list
    if (this._overflow === true && this._useCarousel === true) {
      if (this._backwardLimit === true) {
        addClass(this._backwards, 'spark-panel-carousel__limit');
      } else {
        removeClass(this._backwards, 'spark-panel-carousel__limit');
      }

      if (this._forwardLimit === true) {
        addClass(this._forwards, 'spark-panel-carousel__limit');
      } else {
        removeClass(this._forwards, 'spark-panel-carousel__limit');
      }

      if (this._useControls === false) {
        addClass(this._backwards, 'spark-panel-carousel-no-controls');
        addClass(this._forwards, 'spark-panel-carousel-no-controls');
      } else {
        removeClass(this._backwards, 'spark-panel-carousel-no-controls');
        removeClass(this._forwards, 'spark-panel-carousel-no-controls');
      }
    } else {
      addClass(this._backwards, 'spark-panel-carousel__limit');
      addClass(this._forwards, 'spark-panel-carousel__limit');
    }
  }


  /**
   * Reverse the carousel one items.
   * @param  {clickEvent} _event click
   * @return {Number}
   */
  _singleBackwardClick(_event) {
    const button = _event.currentTarget;
    const buttonWidth = outerWidth(button);
    const buttonLeft = offset(button).left;
    const left = offset(this._panelCarouselList).left;
    let xPosition = 0;

    for (let i = this._panelCarouselItems.length - 1; i > 0; i--) {
      if (offset(this._panelCarouselItems[i]).left < buttonLeft + buttonWidth) {
        xPosition = left - offset(this._panelCarouselItems[i]).left + buttonWidth;

        // this is the margin of error so that if the browser size so it looks better at a browser size that may cause a button click to show a couple pixels
        if (xPosition > buttonWidth - 2) {
          xPosition = 0;
        }
        break;
      }
    }
    return xPosition;
  }


  /**
   * Advance the carousel one item.
   * @param  {clickEvent} _event click
   * @return {Number}
   */
  _singleForwardClick(_event) {
    const button = _event.currentTarget;
    let xPosition = 0;
    const buttonWidth = outerWidth(button);
    let maxLeft = this._panelCarouselWidth + offset(this.el).left;
    let totalWidth = 0;

    if (this._useControls === true) {
      maxLeft -= buttonWidth;
    }

    xPosition = maxLeft;
    const adjustLeft = offset(this._panelCarouselItems[0]).left - offset(this.el).left;

    for (let i = 0; i < this._panelCarouselItems.length; i++) {
      totalWidth += outerWidth(this._panelCarouselItems[i]);
    }

    this.maxX = Math.round(-totalWidth + this._panelCarouselWidth);
    xPosition = this.maxX;

    for (let i = 0; i < this._panelCarouselItems.length; i++) {
      const itemRight = offset(this._panelCarouselItems[i]).left + outerWidth(this._panelCarouselItems[i]);

      if (itemRight > maxLeft) {
        xPosition = maxLeft - itemRight + adjustLeft;

        if (this._panelCarouselItems[i + 1] === undefined) {
          if (this._useControls === true) {
            xPosition += buttonWidth;
          }
          this.maxX = Math.round(xPosition);
        }

        break;
      }
    }

    if (this.maxX === xPosition) {
      this._forwardLimit = true;
    }

    return xPosition;
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
PanelCarousel.prototype._whitelistedParams = [
  'singleCarouselAdvance',
  'noCarouselBreakPoint',
  'controlBreakPoint',
  'nextLabel',
  'previousLabel',
  'hasTabs'
];


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
PanelCarousel.prototype.defaults = {
  el: null,
  singleCarouselAdvance: false,
  noCarouselBreakPoint: null,
  controlBreakPoint: 'xs',
  nextLabel: 'Show next',
  previousLabel: 'Show previous',
  _useControls: true,
  _panelCarouselItemsWidth: 0,
  _panelCarouselWidth: 0,
  _positionX: 0,
  _forwardLimit: false,
  _backwardLimit: true,
  _overflow: false,
  _hasTabs: false
};

export default PanelCarousel;
